import {
  CheckIcon,
  Divider,
  ErrorfilledIcon,
  GKButton,
  GKModal,
  GKTypography,
  Grid2,
  LeftIcon,
  ResetIcon,
  RightIcon,
  Show,
  Slide,
  SuccessFilledIcon,
  Typography,
} from "components";
import RiskProfileBar from "components/RiskProfileBar";
import { useMutation } from "hooks/useQueries";
import { split } from "lodash";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import { useInvestorContext } from "./InvestorContext";

export const handleReturnRiskColors = (text: any) => {
  switch (text.toLowerCase()) {
    case "moderate":
      return "#ECC704";
    case "low":
      return "#E04101";
    case "high":
      return "#46A900";
    default:
      return "var(--black)";
  }
};

interface Props {
  basketId: string;
  handleNext: () => void;
}

const AssessmentTab = (props: Props) => {
  const form = useFormContext();

  const {
    state: { token },
  } = useInvestorContext();

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [checked, setChecked] = useState(true);
  const [answers, setAnswers] = useState<any[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const retakeAssessmentWatch = form.watch("retakeAssessment");
  form.watch("clientObject.riskprofileDetails");

  const current =
    form.getValues(
      "clientObject.riskprofileQuestionnaire.data.riskQuestions",
    ) &&
    form.getValues("clientObject.riskprofileQuestionnaire.data.riskQuestions")[
      currentQuestion
    ];

  const { mutate: handleRiskProfiling } = useMutation({
    mutationKey: ["RISK_PROFILE_CREATE"],
    mutationFn: ({ clientId, basketId }: { clientId: any; basketId: any }) =>
      axiosInstance.post(
        `/client-basket/${clientId}/onboard-risk-response/?basket_id=${basketId}`,
        {
          client: clientId,
          response: answers,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
            "x-gridkey-user-role": "client",
          },
        },
      ),
    onSuccess: () => {
      form.setValue("retakeAssessment", false);
      setAssessmentCompleted(true);
      refetchQuery("GET_CLIENT_FOR_CREATION");
      setChecked(true);
      setSelectedOption(null);
    },
  });

  const handleNextQuestion = (option: any) => {
    setChecked(false);
    setAnswers((prevAnswers: any) => [
      ...prevAnswers,
      {
        question: current?.id,
        option: option.id,
        optionPoints: option.optionPoints,
      },
    ]);
    if (
      currentQuestion + 1 ===
      form.getValues("clientObject.riskprofileQuestionnaire.data.riskQuestions")
        .length
    ) {
      handleRiskProfiling({
        clientId: form.getValues("clientObject.id"),
        basketId: props.basketId,
      });
    } else {
      setTimeout(() => {
        setChecked(true);
        setCurrentQuestion((prev) => prev + 1);
      }, 300);
    }
  };

  const riskProfileMatch =
    +form.getValues("clientObject.riskprofileDetails.points") >
    +split(
      form.getValues("clientObject.basket.riskProfilePoints") || "0 - 0",
      " ",
    )[0];

  return (
    <Grid2 container spacing={2} p={0.5}>
      <Grid2 size={7} display={"flex"} flexDirection={"column"} gap={1}>
        <Typography
          fontSize={20}
          fontWeight={600}
          // pt={2}
          sx={{ fontSize: "clamp(1.125rem, 0.6563rem + 1.5vw, 1.5rem)" }}
        >
          Let&apos;s understand your risk appetite!
        </Typography>
        <Typography fontSize={12} fontWeight={500} color="var(--neutral-500)">
          Tell us a bit about yourself, and we&apos;ll match you with the best
          investment approach.
        </Typography>
      </Grid2>
      <Grid2
        size={5}
        display={"flex"}
        justifyContent={"flex-end"}
        alignItems={"start"}
      >
        <img
          src="/Logos/iconFullLogo.png"
          alt="GridKey Logo"
          style={{
            height: "32px",
            width: "auto",
          }}
        />
      </Grid2>
      <Show.When isTrue={retakeAssessmentWatch}>
        <Grid2 size={12} mt={2}>
          <Typography fontSize={12} fontWeight={600} color="var(--neutral-500)">
            QUESTION: {currentQuestion + 1}/
            {
              form.getValues()?.clientObject?.riskprofileQuestionnaire?.data
                ?.riskQuestions?.length
            }
          </Typography>
          <Show.When isTrue={retakeAssessmentWatch}>
            <Grid2 size={12} height={"320px"} pt={2.5}>
              <div style={{ overflow: "none" }}>
                {form.getValues(
                  "clientObject.riskprofileQuestionnaire.data.riskQuestions",
                )?.length && (
                  <Slide
                    direction="left"
                    in={checked}
                    mountOnEnter
                    unmountOnExit
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: "column",
                        gap: 16,
                        overflow: "none",
                      }}
                    >
                      <GKTypography
                        fontSize={16}
                        fontWeight={600}
                        color="var(--grey-900)"
                        display={"flex"}
                        // justifyContent={"center"}
                      >
                        {currentQuestion + 1}. {current.questionText}
                      </GKTypography>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          // border: "0.5px solid #e0e0e0",
                          // borderRadius: 12,
                          overflow: "hidden",
                        }}
                      >
                        {current.options.map((opt: any, index: number) => {
                          const isSelected = selectedOption === opt.option;
                          return (
                            <div key={index}>
                              {index !== 0 && (
                                <div
                                  style={{
                                    height: 1,
                                    background: isSelected
                                      ? "#e8f0f5"
                                      : "var(--color-background-primary)",
                                  }}
                                />
                              )}
                              <label
                                htmlFor={`question-${index}`}
                                className={`option-row ${isSelected ? "selected" : ""}`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 14,
                                  padding: "14px 16px",
                                  cursor: "pointer",
                                  borderRadius: 4,
                                  background: isSelected
                                    ? "var(--primary-colors-primary-50, #E6F0F3)"
                                    : "transparent",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background =
                                      "var(--neutral-colors-neutral-50, #EEF5FA)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background =
                                      "transparent";
                                  }
                                }}
                              >
                                <input
                                  id={`question-${index}`}
                                  type="radio"
                                  name="question"
                                  value={opt.option}
                                  checked={isSelected}
                                  onChange={() => {
                                    setSelectedOption(opt.option);
                                    const isLastQuestion =
                                      currentQuestion + 1 ===
                                      form.getValues(
                                        "clientObject.riskprofileQuestionnaire.data.riskQuestions",
                                      )?.length;

                                    if (!isLastQuestion) {
                                      handleNextQuestion(opt);
                                    }
                                  }}
                                  style={{
                                    position: "absolute",
                                    opacity: 0,
                                    pointerEvents: "none",
                                  }}
                                />

                                {/* Custom radio circle */}
                                <div
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    border: isSelected
                                      ? "none"
                                      : "2px solid #c0c0c0",
                                    background: isSelected ? "#1e5f74" : "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isSelected && (
                                    <div
                                      style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        background: "#fff",
                                      }}
                                    />
                                  )}
                                </div>

                                <span
                                  style={{
                                    fontSize: 15,
                                    fontWeight: isSelected ? 500 : 400,
                                    color: isSelected
                                      ? "var(--primary-colors-primary-900)"
                                      : "#1a1a1a",
                                  }}
                                >
                                  {opt.option}
                                </span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Slide>
                )}
              </div>
            </Grid2>
            <Grid2
              size={12}
              gap={1}
              display={"flex"}
              justifyContent={"space-between"}
              mt={2.5}
            >
              <Grid2 size={6} display={"flex"} gap={1}>
                <GKButton
                  variant="outlined"
                  color="dark"
                  onClick={() => setCurrentQuestion((p) => p - 1)}
                  disabled={currentQuestion === 0}
                >
                  <LeftIcon />
                </GKButton>
                <GKButton
                  variant="outlined"
                  color="dark"
                  onClick={() => setCurrentQuestion((p) => p + 1)}
                  disabled={
                    form.getValues(
                      "clientObject.riskprofileQuestionnaire.data.riskQuestions",
                    )?.length -
                      1 ===
                    currentQuestion
                  }
                >
                  <RightIcon />
                </GKButton>
              </Grid2>
              <Show.When
                isTrue={
                  currentQuestion + 1 ===
                    form.getValues(
                      "clientObject.riskprofileQuestionnaire.data.riskQuestions",
                    )?.length && selectedOption !== null
                }
              >
                <Grid2 size={6} display={"flex"} justifyContent={"flex-end"}>
                  <GKButton
                    variant="contained"
                    color="primary"
                    startIcon={<CheckIcon />}
                    onClick={() => {
                      const current = form.getValues(
                        "clientObject.riskprofileQuestionnaire.data.riskQuestions",
                      )[currentQuestion];
                      const selectedOptionObj = current.options.find(
                        (opt: any) => opt.option === selectedOption,
                      );

                      setAnswers((prevAnswers: any) => [
                        ...prevAnswers,
                        {
                          question: current?.id,
                          option: selectedOptionObj.id,
                          optionPoints: selectedOptionObj.optionPoints,
                        },
                      ]);

                      handleRiskProfiling({
                        clientId: form.getValues("clientObject.id"),
                        basketId: props.basketId,
                      });
                    }}
                  >
                    Submit
                  </GKButton>
                </Grid2>
              </Show.When>
            </Grid2>
          </Show.When>
        </Grid2>
      </Show.When>

      <Show.When
        isTrue={
          form.getValues("clientObject.riskprofileDetails.points") ||
          (assessmentCompleted && !retakeAssessmentWatch)
        }
      >
        <Grid2 container size={12} py={2}>
          <Grid2 size={12} display="flex">
            <Typography fontSize={14} fontWeight={500} color="var(--grey-900)">
              Your Risk Score is{" "}
              <Typography component="span" fontWeight={600}>
                {form.getValues("clientObject.riskprofileDetails.points")}
              </Typography>{" "}
              & your Risk Profile is{" "}
              <Typography
                component="span"
                fontWeight={600}
                color={handleReturnRiskColors(
                  form.getValues(
                    "clientObject.riskprofileDetails.riskStatus",
                  ) || "-",
                )}
              >
                {form.getValues("clientObject.riskprofileDetails.riskStatus")}
              </Typography>
            </Typography>
          </Grid2>
        </Grid2>

        <Grid2 size={12} py={2}>
          <RiskProfileBar
            lowPoints={form.getValues(
              "clientObject.riskprofileQuestionnaire.data.lowPoints",
            )}
            highPoints={form.getValues(
              "clientObject.riskprofileQuestionnaire.data.highPoints",
            )}
            pointAchieved={form.getValues(
              "clientObject.riskprofileDetails.points",
            )}
          />
        </Grid2>

        <Grid2
          size={12}
          py={3}
          px={2}
          display="flex"
          gap={2}
          borderRadius={3}
          alignItems="center"
          bgcolor={
            riskProfileMatch ? "var(--success-100)" : "var(--warning-100)"
          }
        >
          {riskProfileMatch ? (
            <SuccessFilledIcon
              width={30}
              height={30}
              color="var(--success-700)"
            />
          ) : (
            <ErrorfilledIcon
              width={30}
              height={30}
              color="var(--warning-700)"
            />
          )}
          <Typography
            fontWeight={600}
            width={"330px"}
            color={
              riskProfileMatch ? "var(--success-700)" : "var(--warning-700)"
            }
          >
            {riskProfileMatch
              ? "Great news! Your risk profile matches the basket. You can proceed with the Onboarding."
              : "Your Risk Profile doesn’t match with the basket’s Risk Profile. Do you still want to proceed?"}
          </Typography>
        </Grid2>

        <Grid2 size={12} mt={3.5}>
          <Typography
            mb={1}
            fontSize={12}
            fontWeight={500}
            color="var(--grey-900)"
          >
            How is my risk profile decided:
          </Typography>
          <Grid2
            size={12}
            display="flex"
            flexDirection="column"
            gap={1}
            p={3}
            bgcolor={"var(--grey-bg-100)"}
          >
            {(() => {
              const details = form.getValues("clientObject.riskprofileDetails");
              const labels: string[] = details?.pointsDetails || [];
              const pointsRange: string[] = details?.pointsRange || [];
              const riskStatus: string = details?.riskStatus || "";

              return labels.map((label: string, index: number) => (
                <Grid2 key={index}>
                  <Grid2 size={12} display="flex" alignItems="center" gap={1}>
                    <Typography
                      fontSize={12}
                      fontWeight={400}
                      color="var(--grey-700)"
                      width={"110px"}
                    >
                      {label.charAt(0).toUpperCase() + label.slice(1)}:
                    </Typography>
                    <Typography
                      fontSize={12}
                      fontWeight={600}
                      color="var(--grey-700)"
                    >
                      {pointsRange[index] || "-"}
                    </Typography>
                    {label.toLowerCase() === riskStatus.toLowerCase() && (
                      <CheckIcon fontSize={16} color="var(--success-700)" />
                    )}
                  </Grid2>
                  {index < labels.length - 1 && (
                    <Divider sx={{ py: 0.5, my: 0.5 }} />
                  )}
                </Grid2>
              ));
            })()}
          </Grid2>
          {riskProfileMatch && (
            <Grid2
              size={12}
              display={"flex"}
              justifyContent={"flex-end"}
              mt={2}
            >
              <GKButton
                variant="outlined"
                color="dark"
                startIcon={<ResetIcon />}
                onClick={() => {
                  form.setValue("retakeAssessment", true);
                  form.setValue("clientObject.riskprofileDetails", null);
                  setAssessmentCompleted(false);
                }}
              >
                Retake Assessment
              </GKButton>
            </Grid2>
          )}
          {!riskProfileMatch && (
            <Grid2
              size={12}
              display={"flex"}
              gap={1}
              justifyContent={"space-between"}
              alignItems={"center"}
              mt={2}
            >
              <GKButton
                variant="text"
                color="error"
                onClick={() => setCancelModalOpen(true)}
              >
                No, Cancel Process
              </GKButton>
              <GKButton
                variant="outlined"
                color="dark"
                startIcon={<ResetIcon />}
                onClick={() => {
                  form.setValue("retakeAssessment", true);
                  form.setValue("clientObject.riskprofileDetails", null);
                }}
              >
                Retake Assessment
              </GKButton>
            </Grid2>
          )}
        </Grid2>
        {cancelModalOpen && (
          <GKModal
            width={"426px"}
            open={cancelModalOpen}
            setOpen={setCancelModalOpen}
            modalTitle=""
            footer={
              <>
                <Grid2 size={12} display={"flex"} gap={2}>
                  <GKButton
                    fullWidth
                    variant="text"
                    color="error"
                    onClick={() => setCancelModalOpen(false)}
                  >
                    No, Cancel Process
                  </GKButton>
                  <GKButton
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<CheckIcon />}
                    onClick={() => {
                      setCancelModalOpen(false);
                      form.setValue("retakeAssessment", true);
                      form.setValue("clientObject.riskprofileDetails", null);
                    }}
                  >
                    Retake Assessment
                  </GKButton>
                </Grid2>
              </>
            }
          >
            <Grid2
              size={12}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              flexDirection={"column"}
            >
              <Grid2>
                <ErrorfilledIcon
                  width={50}
                  height={50}
                  color="var(--warning-700)"
                />
              </Grid2>
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                textAlign={"center"}
                mt={2}
              >
                <Typography
                  fontSize={16}
                  fontWeight={700}
                  color="var(--black-900)"
                >
                  Do you want to cancel or retake the assessment?
                </Typography>
              </Grid2>
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                mt={1}
              >
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--neutral-500)"
                  textAlign={"center"}
                >
                  Canceling will end your subscription, and you&apos;ll have to
                  restart if you want to subscribe again.
                </Typography>
              </Grid2>
            </Grid2>
          </GKModal>
        )}

        {/* <Grid2 size={12} textAlign={"center">}>
            {riskProfileMatch ? (
              <img src="/investor/risk-success.svg" alt="" />
            ) : (
              <img src="/investor/risk-error.svg" alt="" />
            )}
          </Grid2> */}

        {/* <Grid2 size={12} textAlign={"center"}>
          <Typography fontWeight={600}>
            {riskProfileMatch
              ? "Congratulations! Your risk profile matches with the basket's risk profile, we can move ahead with the subscription."
              : "Your risk profile doesn't match with the basket's risk profile.Do you still want to proceed?"}
          </Typography>
        </Grid2>

        <Grid2
          size={12}
          display={"flex"}
          gap={1}
          justifyContent={"center"}
          mt={2}
        >
          <GKButton
            variant="contained"
            onClick={() => {
              handleRiskProfiling({
                clientId: form.getValues("clientObject.id"),
              });
            }}
          >
            Next
          </GKButton>
          <GKButton
            variant="outlined"
            color="dark"
            onClick={() => {
              form.setValue("retakeAssessment", true);
              form.setValue("clientObject.riskprofileDetails", null);
              setAssessmentCompleted(false);
            }}
          >
            Retake Assessment
          </GKButton>
        </Grid2>

        {!riskProfileMatch && (
          <Grid2
            size={12}
            display={"flex"}
            gap={1}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <GKButton variant="contained" onClick={() => props.handleNext()}>
              Yes
            </GKButton>
            <GKButton
              variant="outlined"
              color="dark"
              onClick={() => {
                form.setValue("retakeAssessment", true);
                form.setValue("clientObject.riskprofileDetails", null);
                setAssessmentCompleted(false);
              }}
            >
              Retake Assessment
            </GKButton>
          </Grid2>
        )} */}

        {/* <Grid2 container size={12} p={2} bgcolor={"#F8FAFC"}>
          <Grid2 size={12} bgcolor={"#F8FAFC"}>
            <Typography
              color={blueGrey[600]}
              display={"flex"}
              justifyContent={"center"}
              gap={0.5}
            >
              Your risk score is
              <Typography fontWeight={600}>
                {form.getValues("clientObject.riskprofileDetails.points")}
              </Typography>
              and risk profile status is
              <Typography
                fontWeight={600}
                color={handleReturnRiskColors(
                  form.getValues(
                    `clientObject.riskprofileDetails.riskStatus`,
                  ) || "-",
                )}
              >
                {form.getValues(`clientObject.riskprofileDetails.riskStatus`)}
              </Typography>
            </Typography>
          </Grid2>
        </Grid2>
        <Grid2 size={12} container bgcolor={"#F8FAFC"} p={4} borderRadius={2}>
          <Grid2 size={12} position={"relative"}>
            <Box
              sx={{
                width: "100%",
                position: "relative",
                background: `linear-gradient(90deg, rgba(255,0,0,1) 0%, rgba(235,233,58,1) 51%, rgba(141,224,43,1) 100%)`,
                height: 10,
                borderRadius: 2,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  height: "150%",
                  width: "2px",
                  background: "var(--black)",
                  top: -2,
                  left: `${
                    ((Number(
                      form.getValues("clientObject.riskprofileDetails.points"),
                    ) -
                      Number(
                        form.getValues(
                          "clientObject.riskprofileQuestionnaire.data.lowPoints",
                        ),
                      )) /
                      (Number(
                        form.getValues(
                          "clientObject.riskprofileQuestionnaire.data.highPoints",
                        ),
                      ) -
                        Number(
                          form.getValues(
                            "clientObject.riskprofileQuestionnaire.data.lowPoints",
                          ),
                        ))) *
                    100
                  }%`,
                  transform: "translateX(-50%)",
                },
                "&::after": {
                  content: `"${form.getValues(
                    "clientObject.riskprofileDetails.points",
                  )}"`,
                  position: "absolute",
                  height: "100%",
                  top: 16,
                  fontSize: 12,
                  fontWeight: 600,
                  left: `${
                    ((Number(
                      form.getValues("clientObject.riskprofileDetails.points"),
                    ) -
                      Number(
                        form.getValues(
                          "clientObject.riskprofileQuestionnaire.data.lowPoints",
                        ),
                      )) /
                      (Number(
                        form.getValues(
                          "clientObject.riskprofileQuestionnaire.data.highPoints",
                        ),
                      ) -
                        Number(
                          form.getValues(
                            "clientObject.riskprofileQuestionnaire.data.lowPoints",
                          ),
                        ))) *
                    100
                  }%`,
                  transform: "translateX(-50%)",
                },
              }}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: -20,
                  left: 0,
                  fontSize: 12,
                  color: "#9AA4B2",
                }}
              >
                {form.getValues(
                  "clientObject.riskprofileQuestionnaire.data.lowPoints",
                )}
              </div>
              <div
                style={{
                  position: "absolute",
                  top: -20,
                  left: 0,
                  color: "#9AA4B2",
                  fontSize: 12,
                }}
              >
                {form.getValues(
                  `clientObject.riskprofileDetails.pointsDetails`,
                ) &&
                  form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  )[0]}
              </div>
              <Show.When
                isTrue={
                  form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  )?.length === 3
                }
              >
                <div
                  style={{
                    position: "absolute",
                    bottom: -20,
                    right: 0,
                    color: "#9AA4B2",
                    fontSize: 12,
                  }}
                >
                  {form.getValues(
                    "clientObject.riskprofileQuestionnaire.data.highPoints",
                  )}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: -20,
                    left: 0,
                    fontSize: 12,
                    color: "#9AA4B2",
                    right: 0,
                    marginInline: "auto",
                    width: "fit-content",
                  }}
                >
                  {form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  ) &&
                    form.getValues(
                      `clientObject.riskprofileDetails.pointsDetails`,
                    )[1]}
                </div>
                <div
                  style={{
                    position: "absolute",
                    color: "#9AA4B2",
                    top: -20,
                    right: 0,
                    fontSize: 12,
                  }}
                >
                  {form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  ) &&
                    form.getValues(
                      `clientObject.riskprofileDetails.pointsDetails`,
                    )[2]}
                </div>
              </Show.When>
              <Show.When
                isTrue={
                  form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  )?.length === 5
                }
              >
                <div
                  style={{
                    position: "absolute",
                    bottom: -20,
                    right: 0,
                    color: "#9AA4B2",
                    fontSize: 12,
                  }}
                >
                  {form.getValues(
                    "clientObject.riskprofileQuestionnaire.data.highPoints",
                  )}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: -20,
                    left: "25%",
                    fontSize: 12,
                    color: "#9AA4B2",
                  }}
                >
                  {form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  ) &&
                    form.getValues(
                      `clientObject.riskprofileDetails.pointsDetails`,
                    )[1]}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: -20,
                    left: "50%",
                    fontSize: 12,
                    color: "#9AA4B2",
                  }}
                >
                  {form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  ) &&
                    form.getValues(
                      `clientObject.riskprofileDetails.pointsDetails`,
                    )[2]}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: -20,
                    left: "74%",
                    fontSize: 12,
                    color: "#9AA4B2",
                  }}
                >
                  {form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  ) &&
                    form.getValues(
                      `clientObject.riskprofileDetails.pointsDetails`,
                    )[3]}
                </div>
                <div
                  style={{
                    position: "absolute",
                    color: "#9AA4B2",
                    top: -20,
                    right: 0,
                    fontSize: 12,
                  }}
                >
                  {form.getValues(
                    `clientObject.riskprofileDetails.pointsDetails`,
                  ) &&
                    form.getValues(
                      `clientObject.riskprofileDetails.pointsDetails`,
                    )[4]}
                </div>
              </Show.When>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 size={12} textAlign={"center"}>
          {riskProfileMatch ? (
            <img src="/investor/risk-success.svg" alt="" />
          ) : (
            <img src="/investor/risk-error.svg" alt="" />
          )}
        </Grid2>
        <Grid2 size={12} textAlign={"center"}>
          <Typography fontWeight={600}>
            {riskProfileMatch
              ? "Congratulations! Your risk profile matches with the basket’s risk profile, we can move ahead with the subscription."
              : "Your risk profile doesn't match with the basket’s risk profile.Do you still want to proceed?"}
          </Typography>
        </Grid2>
        {!riskProfileMatch && (
          <Grid2
            size={12}
            display={"flex"}
            gap={1}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <GKButton variant="contained" onClick={() => props.handleNext()}>
              Yes
            </GKButton>
            <GKButton
              variant="outlined"
              color="dark"
              onClick={() => {
                form.setValue("retakeAssessment", true);
                form.setValue("clientObject.riskprofileDetails", null);
              }}
            >
              Retake Assessment
            </GKButton>
          </Grid2>
        )} */}
      </Show.When>
    </Grid2>
  );
};

export default AssessmentTab;
